import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { canUseEngine, engineLabel, type Engine } from 'api/analysisApi';
import { useAuthStore } from 'store/useAuthStore';
import type { ForkSweepFilters } from 'api/forkApi';
import { useForkSweep } from 'hooks/useFork';
import type { AnalysisHistoryFilters } from 'types/history';
import './fork.css';

/** Replay targets. v0/v0.1 read screenshots, so they cannot take a data set. */
const FORKABLE: Engine[] = ['brainer_v1', 'brainer_v1_nohm', 'enforcer', 'enforcer_v3', 'petar_v4', 'stojan_v5', 'claude'];

/**
 * Parent scoping for a sweep.
 *
 * Deliberately drops verdict/grade/side/trend: those describe a run's *output*,
 * and selecting parents by the answer their old engine gave would hand the new
 * engine a set pre-filtered on the very thing being measured.
 */
function sweepFilters(filters: AnalysisHistoryFilters): ForkSweepFilters {
	const { asset, engine, exchange, market, timeframe, since, until } = filters;
	return { asset, engine, exchange, market, timeframe, since, until };
}

interface ForkSweepBarProps {
	/** The History filters currently on screen — the sweep covers exactly this set. */
	filters: AnalysisHistoryFilters;
	/** Called when a sweep finishes, so the table can pick up the new rows. */
	onFinished?: () => void;
}

/**
 * Batch-fork every analysis matching the current filters onto one engine.
 *
 * Preview is a mandatory step rather than a nicety: a sweep is unattended spend,
 * and a couple of hundred v1 forks is three figures. The cost has to be on screen
 * before the click that commits to it.
 */
export function ForkSweepBar({ filters, onFinished }: ForkSweepBarProps) {
	const [open, setOpen] = useState(false);
	const user = useAuthStore((s) => s.user);
	const targets = FORKABLE.filter((e) => canUseEngine(user, e));
	const [engine, setEngine] = useState<Engine>('enforcer');
	const [repeats, setRepeats] = useState(1);
	const [limit, setLimit] = useState(25);
	const sweep = useForkSweep();

	const heatmap = engine !== 'brainer_v1_nohm';
	const apiEngine: Engine = heatmap ? engine : 'brainer_v1';
	const body = { engine: apiEngine, heatmap, repeats, limit };

	const running = sweep.state?.status === 'running';
	const finishedRef = React.useRef(false);
	React.useEffect(() => {
		if (running) {
			finishedRef.current = false;
			return;
		}
		// Fire once per completed sweep, not on every idle render.
		if (!finishedRef.current && sweep.state && sweep.state.created > 0) {
			finishedRef.current = true;
			onFinished?.();
		}
	}, [running, sweep.state, onFinished]);

	const openDialog = () => {
		setOpen(true);
		sweep.loadPreview(body, sweepFilters(filters));
	};

	const reprice = (next: Partial<typeof body>) => {
		sweep.loadPreview({ ...body, ...next }, sweepFilters(filters));
	};

	if (running) {
		const st = sweep.state!;
		return (
			<div className='bt-run'>
				<span className='fork-sweep-progress'>
					Forking {st.processed}/{st.total}
					{st.failed > 0 ? ` · ${st.failed} failed` : ''}
					{st.cost_usd > 0 ? ` · $${st.cost_usd.toFixed(2)}` : ''}
				</span>
				<button className='fork-sweep-cancel' onClick={sweep.cancel}>
					Stop
				</button>
			</div>
		);
	}

	return (
		<>
			<button
				className='fork-sweep-btn'
				onClick={openDialog}
				title="Replay every analysis matching the current filters through another engine, so the engines can be compared on identical inputs"
			>
				Fork these…
			</button>

			{open &&
				createPortal(
					<div className='fork-dialog-backdrop' onMouseDown={() => setOpen(false)}>
						<div className='fork-dialog' onMouseDown={(e) => e.stopPropagation()}>
							<div className='fork-dialog-title'>Fork the filtered analyses</div>

							<p className='fork-note'>
								Each matching run's stored input is replayed through the engine below —
								no market data is fetched, and each fork is scored against the candles
								that followed <em>its own</em> parent. Forks are hidden from this table
								by default.
							</p>

							<div className='fork-controls'>
								<label className='fork-field'>
									<span className='fork-field-label'>Engine</span>
									<select
										className='fork-select'
										value={engine}
										onChange={(e) => {
											const next = e.target.value as Engine;
											setEngine(next);
											const hm = next !== 'brainer_v1_nohm';
											reprice({ engine: hm ? next : 'brainer_v1', heatmap: hm });
										}}
									>
										{targets.map((v) => (
											<option key={v} value={v}>
												{engineLabel(v)}
											</option>
										))}
									</select>
								</label>
								<label className='fork-field fork-field-narrow'>
									<span className='fork-field-label'>Max rows</span>
									<input
										className='fork-num'
										type='number'
										min={1}
										max={500}
										value={limit}
										onChange={(e) => {
											const v = Math.min(500, Math.max(1, Number(e.target.value) || 1));
											setLimit(v);
											reprice({ limit: v });
										}}
									/>
								</label>
								<label className='fork-field fork-field-narrow'>
									<span className='fork-field-label'>Runs each</span>
									<input
										className='fork-num'
										type='number'
										min={1}
										max={10}
										value={repeats}
										onChange={(e) => {
											const v = Math.min(10, Math.max(1, Number(e.target.value) || 1));
											setRepeats(v);
											reprice({ repeats: v });
										}}
									/>
								</label>
							</div>

							{sweep.previewing && <div className='fork-note'>Checking…</div>}

							{sweep.preview && !sweep.previewing && (
								<>
									<div>
										<div className='fork-dialog-cost'>
											{sweep.preview.est_cost_usd != null
												? `≈ $${sweep.preview.est_cost_usd.toFixed(2)}`
												: 'Cost unknown'}
										</div>
										<div className='fork-dialog-cost-sub'>
											{sweep.preview.eligible} analyses × {repeats} run
											{repeats === 1 ? '' : 's'} = {sweep.preview.runs} Claude call
											{sweep.preview.runs === 1 ? '' : 's'}
											{sweep.preview.est_cost_usd == null &&
												' — this engine has no priced history to estimate from'}
										</div>
									</div>

									{sweep.preview.ineligible > 0 && (
										<div className='fork-dialog-reasons'>
											{sweep.preview.ineligible} row
											{sweep.preview.ineligible === 1 ? '' : 's'} skipped:{' '}
											{Object.entries(sweep.preview.ineligible_reasons)
												.map(([reason, n]) => `${n} ${reason.replace(/_/g, ' ')}`)
												.join(', ')}
										</div>
									)}

									{repeats === 1 && sweep.preview.eligible < 20 && (
										<div className='fork-dialog-reasons'>
											A thin sample: v1 can flip direction run-to-run on identical
											input, so a handful of forks measures that noise as much as the
											engine. Widen the filters or raise <em>Runs each</em> before
											reading the comparison as a verdict.
										</div>
									)}
								</>
							)}

							{sweep.error && <div className='fork-error'>{sweep.error}</div>}

							<div className='fork-dialog-actions'>
								<button
									className='fork-dialog-cancel'
									onClick={() => {
										setOpen(false);
										sweep.clearPreview();
									}}
								>
									Cancel
								</button>
								<button
									className='fork-dialog-confirm'
									disabled={!sweep.preview || sweep.preview.eligible === 0 || sweep.previewing}
									onClick={() => {
										sweep.start(body, sweepFilters(filters));
										setOpen(false);
									}}
								>
									{sweep.preview?.eligible === 0
										? 'Nothing to fork'
										: `Fork ${sweep.preview?.runs ?? 0}`}
								</button>
							</div>
						</div>
					</div>,
					document.body,
				)}
		</>
	);
}
